<script setup lang="ts">
import type { NativeAdData } from '~/utils/ad-manager'
import { onMounted, watch } from 'vue'
import { useAdManager } from '~/composables/useAdManager'

interface Props {
  adUnitId?: string
  autoLoad?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  autoLoad: true,
})

const {
  isInitialized,
  isLoading,
  currentAd,
  error,
  isMobile,
  initialize,
  loadAd,
  trackClick,
  trackImpression,
} = useAdManager()

const baseStore = useBaseStore()

// Initialize Ad Manager on mount if on mobile
onMounted(async () => {
  if (isMobile.value && !isInitialized.value) {
    try {
      const config = {
        appId: baseStore.platform === 'ios'
          ? 'ca-app-pub-2471931316522634~3406524209'
          : 'ca-app-pub-2471931316522634~1808966530',
        iosAdUnitId: props.adUnitId || 'ca-app-pub-2471931316522634/4143597203',
        androidAdUnitId: props.adUnitId || 'ca-app-pub-2471931316522634/9121314492',
        testMode: false, // Set to true for testing
      }
      await initialize(config)
    }
    catch (err) {
      console.error('Failed to initialize Ad Manager:', err)
    }
  }

  // Auto-load ad if enabled
  if (props.autoLoad && isMobile.value && isInitialized.value) {
    try {
      await loadAd(props.adUnitId)
    }
    catch (err) {
      console.error('Failed to load ad:', err)
    }
  }
})

// Watch for initialization to auto-load
watch(isInitialized, async (newVal) => {
  if (newVal && props.autoLoad && isMobile.value) {
    try {
      await loadAd(props.adUnitId)
    }
    catch (err) {
      console.error('Failed to load ad:', err)
    }
  }
})

async function handleAdClick(ad: NativeAdData) {
  if (ad.adUnitId) {
    await trackClick(ad.adUnitId)
  }
  // The actual click handling is done by the native SDK
}

async function handleAdVisible(ad: NativeAdData) {
  if (ad.adUnitId) {
    await trackImpression(ad.adUnitId)
  }
}

// Expose load function for manual loading
defineExpose({
  loadAd: () => loadAd(props.adUnitId),
})
</script>

<template>
  <div v-if="isMobile && (currentAd || isLoading || error)" class="native-ad-container">
    <!-- Loading state -->
    <div v-if="isLoading" class="p-4 bg-white border border-poivre-200 rounded-lg shadow-sm">
      <div class="flex items-center justify-center py-8">
        <UIcon name="i-mdi-loading" class="animate-spin text-2xl text-poivre-400" />
        <span class="ml-2 text-sm text-poivre-500">Chargement de la publicité...</span>
      </div>
    </div>

    <!-- Error state -->
    <div v-else-if="error" class="p-4 bg-white border border-poivre-200 rounded-lg shadow-sm">
      <div class="text-center py-4">
        <p class="text-sm text-poivre-500">
          {{ error }}
        </p>
      </div>
    </div>

    <!-- Ad content -->
    <div
      v-else-if="currentAd"
      class="native-ad bg-white border border-poivre-200 rounded-lg shadow-sm overflow-hidden"
      @click="handleAdClick(currentAd)"
    >
      <div class="p-4">
        <!-- Ad label -->
        <div class="mb-2">
          <span class="text-xs font-medium text-poivre-400 uppercase tracking-wide">Publicité</span>
        </div>

        <!-- Ad content layout -->
        <div class="flex gap-4">
          <!-- Icon/Image -->
          <div v-if="currentAd.iconImageUrl" class="flex-shrink-0">
            <img
              :src="currentAd.iconImageUrl"
              :alt="currentAd.advertiser || 'Ad'"
              class="w-16 h-16 object-cover rounded"
              @load="handleAdVisible(currentAd)"
            >
          </div>

          <!-- Text content -->
          <div class="flex-1 min-w-0">
            <!-- Headline -->
            <h3 v-if="currentAd.headline" class="text-base font-semibold text-poivre-800 mb-1 line-clamp-2">
              {{ currentAd.headline }}
            </h3>

            <!-- Body -->
            <p v-if="currentAd.body" class="text-sm text-poivre-600 mb-2 line-clamp-2">
              {{ currentAd.body }}
            </p>

            <!-- Advertiser/Store -->
            <div class="flex items-center gap-2 mb-2">
              <span v-if="currentAd.advertiser" class="text-xs text-poivre-500">
                {{ currentAd.advertiser }}
              </span>
              <span v-if="currentAd.store" class="text-xs text-poivre-500">
                • {{ currentAd.store }}
              </span>
              <span v-if="currentAd.price" class="text-xs font-medium text-poivre-700">
                {{ currentAd.price }}
              </span>
            </div>

            <!-- Call to action -->
            <div v-if="currentAd.callToAction">
              <span class="inline-block px-3 py-1 text-xs font-medium text-white bg-bleuet rounded">
                {{ currentAd.callToAction }}
              </span>
            </div>
          </div>
        </div>

        <!-- Images gallery (if multiple images) -->
        <div v-if="currentAd.images && currentAd.images.length > 1" class="mt-3 grid grid-cols-3 gap-2">
          <img
            v-for="(imageUrl, index) in currentAd.images.slice(0, 3)"
            :key="index"
            :src="imageUrl"
            :alt="`Ad image ${index + 1}`"
            class="w-full h-20 object-cover rounded"
          >
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.native-ad {
  cursor: pointer;
  transition: all 0.2s ease;
}

.native-ad:hover {
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
}

.native-ad-container {
  /* Ensure ads don't break layout */
  min-height: 100px;
}
</style>
